import React from 'react';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import { memberProps, useAuth, useMembersContext } from '../../context';
import { ActionButton, Alert, ViewMembersTableDrawer } from '..';
import { useAlert, useFetch, useToggle } from '../../hooks';

export const ViewMembersTableData: React.FC<memberProps> = React.memo(
    member => {
        const { session } = useAuth();
        const { setAdminMembers, setEmployeeMembers } = useMembersContext();
        const { alert, dispatchAlert, closeAlert } = useAlert();
        const [fetchHandler, loading] = useFetch('/dbAdminEditMember', 'PUT');
        const [drawer, toggleDrawer] = useToggle(false);

        const editMember = React.useCallback(
            async (status, code) => {
                const result = await fetchHandler({
                    data: {
                        member_guid: session.MemberGuid,
                        session_guid: session.SessionGuid,
                        data:
                            status !== null
                                ? {
                                      CompanyCode: member.Company,
                                      Active: status,
                                      MemberGuid: member.Guid
                                  }
                                : { CompanyCode: code, MemberGuid: member.Guid }
                    }
                });
                if (result.success) {
                    dispatchAlert({
                        type: 'UPDATE_ALERT',
                        message:
                            status !== null
                                ? `${
                                      status === 1 ? 'Activated' : 'Deactivated'
                                  } ${member.Name}`
                                : `${member.Name} now works for ${
                                      code === 0
                                          ? 'Prisma'
                                          : code === 1
                                          ? 'Prispek'
                                          : 'Spektra'
                                  }`,
                        severity: 'success'
                    });
                    member.Role === 2
                        ? setEmployeeMembers(prevMembers =>
                              prevMembers.map(prevMember =>
                                  prevMember.Guid === member.Guid
                                      ? status !== null
                                          ? { ...member, Active: status }
                                          : { ...member, Company: code }
                                      : prevMember
                              )
                          )
                        : setAdminMembers(prevMembers =>
                              prevMembers.map(prevMember =>
                                  prevMember.Guid === member.Guid
                                      ? status !== null
                                          ? { ...member, Active: status }
                                          : { ...member, Company: code }
                                      : prevMember
                              )
                          );
                } else {
                    dispatchAlert({
                        type: 'UPDATE_ALERT',
                        message: result.msg,
                        severity: 'error'
                    });
                }
                dispatchAlert({ type: 'OPEN' });
            },
            [
                dispatchAlert,
                member,
                fetchHandler,
                session.MemberGuid,
                session.SessionGuid,
                setAdminMembers,
                setEmployeeMembers
            ]
        );

        return (
            <>
                <Alert closeHandler={closeAlert} {...alert} />
                <TableRow hover>
                    <ViewMembersTableDrawer
                        open={drawer}
                        closeHandler={toggleDrawer}
                        member={member}
                        dispatchAlert={dispatchAlert}
                    />
                    <TableCell
                        component='th'
                        scope='row'
                        onClick={toggleDrawer}
                    >
                        {member.Name} {member.Surname}
                    </TableCell>
                    <TableCell onClick={toggleDrawer}>{member.Email}</TableCell>
                    <TableCell onClick={toggleDrawer}>{member.Phone}</TableCell>
                    <TableCell>
                        <TextField
                            id='standard-select-currency'
                            fullWidth
                            select
                            label='Company:'
                            size='small'
                            value={member.Company}
                            onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                                editMember(null, parseInt(e.target.value));
                            }}
                        >
                            <MenuItem value={0}>Prisma</MenuItem>
                            <MenuItem value={1}>Prispek</MenuItem>
                            <MenuItem value={2}>Spektra</MenuItem>
                        </TextField>
                    </TableCell>
                    <TableCell>
                        {member.Active === 1 ? (
                            <Tooltip title='De-activate' arrow>
                                <ActionButton
                                    Icon={<Check color='success' />}
                                    onClick={() => editMember(0, null)}
                                    loading={loading}
                                    loadingColor='success'
                                />
                            </Tooltip>
                        ) : (
                            <Tooltip title='Activate' arrow>
                                <ActionButton
                                    Icon={<Close color='error' />}
                                    onClick={() => editMember(1, null)}
                                    loading={loading}
                                    loadingColor='error'
                                />
                            </Tooltip>
                        )}
                    </TableCell>
                </TableRow>
            </>
        );
    }
);
